import React, { useEffect, useState } from 'react';
import SectionHeader from '@components/common/SectionHeader';
import CurrentProgressChart from '@components/charts/CurrentProgressChart';
import ProgressGaugeChart from '@components/charts/ProgressGaugeChart';
import {
  getProgressMetric,
  type ProgressMetricKey,
} from '@constants/progressMetrics';
import { useSummaryProgressTerm } from '@hooks/useSummaryProgressTerm';
import {
  mapProgressTermApiToProgressStatusOverview,
  mapProgressTermApiToProgressStatusTables,
} from '@apis/performanceMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import {
  formatNumber,
  formatPercent,
  formatUpdateDate,
  PLACEHOLDER,
} from '@utils/format';
import { notify } from '@utils/notify';
import { formatProgressTableHeader } from '@utils/progressTableLabels';
import type {
  ProgressTableCellLine,
  ProgressTableData,
  ProgressStatus,
} from '@app-types/performance';

export interface ProgressStatusSectionProps {
  data: ProgressStatus;
}

const toProgressCellDisplayText = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed || trimmed === 'NaN' || trimmed === '--%') {
    return PLACEHOLDER;
  }
  return text;
};

/** Section 5: Current progress status (Figma 3490:22070 / mobile 3154:4510). */
const ProgressStatusSection: React.FC<ProgressStatusSectionProps> = ({ data }) => {
  const [activeMetric, setActiveMetric] = useState(() =>
    getProgressMetric(data.activeTabKey),
  );
  type ProgressTableVariant = 'operating-profit' | 'announcement-date';

  useEffect(() => {
    setActiveMetric(getProgressMetric(data.activeTabKey));
  }, [data.activeTabKey]);

  const progressTermQuery = useSummaryProgressTerm(activeMetric);

  useEffect(() => {
    if (progressTermQuery.isError && shouldNotifyDataRequestError(progressTermQuery.error)) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(progressTermQuery.error) ??
          '今期進捗状況の取得に失敗しました。',
      });
    }
  }, [progressTermQuery.error, progressTermQuery.isError]);

  // Merge overview values (chart + gauge + recent). Undefined query data maps
  // to no-data values so loading does not show mock values.
  const overview = mapProgressTermApiToProgressStatusOverview(
    progressTermQuery.data,
    activeMetric,
    data,
  );

  // Matrix tables are API-backed; static labels/heights/node ids stay from data.
  const tables = mapProgressTermApiToProgressStatusTables(progressTermQuery.data, data);

  const tabNodeIds: Record<ProgressMetricKey, string> = {
    revenue: '3353:28690',
    operating: '3353:28691',
    ordinary: '3353:28692',
    net: '3353:28693',
  };
  const metricLabel = activeMetric.displayLabel;

  const renderCellLines = (lines: ProgressTableCellLine[]) =>
    lines.map((line, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <span key={`${line.text}-${index}`} className={line.tone ? `is-${line.tone}` : undefined}>
        {toProgressCellDisplayText(line.text)}
      </span>
    ));

  const renderProgressTable = (
    table: ProgressTableData,
    title: string,
    headerLabel: string,
    headerNodeId: string,
    tableNodeId: string,
    variant: ProgressTableVariant,
  ) => (
    <div
      className={`progress-status__table-wrap progress-status__table-wrap--${variant}`}
      data-node-id={table.nodeId}
    >
      <table
        className={`progress-status__matrix-table progress-status__matrix-table--${variant}`}
        aria-label={title}
        data-node-id={tableNodeId}
      >
        {variant === 'operating-profit' && (
          <colgroup>
            <col className="progress-status__matrix-col--amount-label" />
            {table.columns.map((_, index) => (
              // Column labels can repeat (e.g. all `--` with no API data).
              // eslint-disable-next-line react/no-array-index-key
              <col key={`amount-value-${index}`} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr data-node-id={headerNodeId}>
            <th
              scope="col"
              className={`progress-status__matrix-cell progress-status__matrix-cell--header${variant === 'operating-profit' ? ' progress-status__matrix-cell--amount-title' : ''}`}
            >
              {headerLabel}
            </th>
            {table.columns.map((column, index) => (
              <th
                // Column labels can repeat (e.g. all `--` with no API data).
                // eslint-disable-next-line react/no-array-index-key
                key={`${tableNodeId}-col-${index}`}
                scope="col"
                className="progress-status__matrix-cell progress-status__matrix-cell--header progress-status__matrix-cell--right"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr
              key={row.key}
              className="progress-status__matrix-row"
              data-node-id={row.nodeId}
              data-row-height={row.height ?? 25}
            >
              <th scope="row" className="progress-status__matrix-cell progress-status__matrix-cell--header">
                {renderCellLines(row.label)}
              </th>
              {row.cells.map((cell, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={`${row.key}-${index}`} className="progress-status__matrix-cell progress-status__matrix-cell--right">
                  {renderCellLines(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <section className="summary-section progress-status" data-node-id="3490:22070">
      <div className="progress-status__head" data-node-id="3124:2693">
        <SectionHeader
          title="今期進捗状況"
          updateDate={`更新日：${formatUpdateDate(overview.updateDate)}`}
          showHelp
          dataNodeId="3124:2694"
        />

        <div
          className="progress-status__tabs"
          role="tablist"
          aria-label="業績指標"
          data-node-id="3353:28689"
        >
          {data.tabs.map((tab) => {
            const isActive = tab.key === activeMetric.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`progress-status__tab${isActive ? ' is-active' : ''}`}
                data-node-id={tabNodeIds[tab.key]}
                onClick={() => setActiveMetric(getProgressMetric(tab.key))}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="progress-status__panel" data-node-id="3490:21971">
        <div className="progress-status__overview" data-node-id="3174:13500">
          <div className="progress-status__chart-column" data-node-id="3174:13759">
            <div className="progress-status__progress-chart" data-node-id="3174:13760">
              <CurrentProgressChart
                max={overview.chart.max}
                unitSuffix={overview.chart.unitSuffix}
                points={overview.chart.points}
                height={198}
              />
            </div>
            <ul className="progress-status__chart-legend" data-node-id="3174:13761">
              <li className="progress-status__chart-legend-item" data-node-id="3174:13762">
                <span className="progress-status__legend-swatch progress-status__legend-swatch--previous" />
                <span>前期</span>
              </li>
              <li className="progress-status__chart-legend-item" data-node-id="3174:13763">
                <span className="progress-status__legend-swatch progress-status__legend-swatch--current" />
                <span>今期</span>
              </li>
              <li className="progress-status__chart-legend-item" data-node-id="3174:13764">
                <span className="progress-status__legend-swatch progress-status__legend-swatch--company" />
                <span>最新会社予想</span>
              </li>
              <li className="progress-status__chart-legend-item" data-node-id="3174:13765">
                <span className="progress-status__legend-triangle" />
                <span>コンセンサス予想</span>
              </li>
            </ul>
          </div>

          <div className="progress-status__rate-column" data-node-id="3174:14209">
            <div className="progress-status__gauge-block" data-node-id="3174:14210">
              <p className="progress-status__gauge-title" data-node-id="3174:14211">
                {metricLabel} 進捗率
              </p>
              <div className="progress-status__gauge" data-node-id="3174:14212">
                <ProgressGaugeChart
                  percent={overview.gauge.percent}
                  centerLabel={overview.gauge.quarterLabel}
                />
              </div>
            </div>

            <div className="progress-status__recent" data-node-id="3174:14213">
              <div className="progress-status__recent-content" data-node-id="3174:14214">
                <p className="progress-status__recent-label" data-node-id="3174:14215">
                  直近実績 (百万円)
                </p>
                <div className="progress-status__recent-table" data-node-id="3174:14216">
                  <div className="progress-status__recent-row" data-node-id="3174:14217">
                    <div className="progress-status__recent-cell progress-status__recent-cell--header">
                      {metricLabel}
                    </div>
                    <div className="progress-status__recent-cell progress-status__recent-cell--value">
                      {formatNumber(overview.recent.value, 0)}
                    </div>
                  </div>
                  <div className="progress-status__recent-row" data-node-id="3174:14218">
                    <div className="progress-status__recent-cell progress-status__recent-cell--header">
                      進捗率
                    </div>
                    <div className="progress-status__recent-cell progress-status__recent-cell--value">
                      {formatPercent(overview.recent.progressPercent, 1)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {renderProgressTable(
          tables.operatingProfit,
          `${metricLabel}推移テーブル`,
          formatProgressTableHeader(
            metricLabel,
            progressTermQuery.data?.currency,
            progressTermQuery.data?.unit,
          ),
          '3124:2696',
          '3155:5723',
          'operating-profit',
        )}
        {renderProgressTable(
          tables.announcementDate,
          '発表日テーブル',
          '発表日',
          '3490:22023',
          '3490:22022',
          'announcement-date',
        )}
      </div>
    </section>
  );
};

export default ProgressStatusSection;
